import { East as EastIcon } from '@mui/icons-material';
import { Card, Typography } from '@mui/material';
import { FieldArray, FormikHelpers, FormikProps } from 'formik';
import { filter, includes, isNumber, map, sortBy, values } from 'lodash';
import { Fragment, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from 'react-query';
import * as Yup from 'yup';

import {
  payoutRequisitesAutomationApi,
  payoutRequisitesBalanceRedistributionRequestsApi,
} from 'api';
import {
  CloseFormikDialogResult,
  DataWrapper,
  DialogProps,
  FormControls,
  FormikCheckbox,
  FormikDialog,
  FormikNumericField,
  FormikSelect,
  TraderSelect,
} from 'components';
import {
  FiatCurrencyCode,
  PayoutRequisitesAutomationType,
  QueryKey,
} from 'enums';
import {
  useCurrencies,
  useMutation,
  usePayoutAutomationUtils,
  useUser,
} from 'hooks';
import { TranslationNamespace } from 'i18n';
import { payoutRequisitesAutomationUtils } from 'utils';

type Props = {} & DialogProps;

type Values = {
  traderId?: string;
  maxBalance: number;
  fromAutomationsMin?: number;
  fromAutomationsMax?: number;
  toAutomationsMin?: number;
  toAutomationsMax?: number;
  fromAutomations: string[];
  toAutomations: string[];
  balanceFiatCurrencyId?: string;
  type: PayoutRequisitesAutomationType;
};

export const RequisitesBalanceRedistributionDialog: React.FC<Props> = ({
  open,
  onClose,
}) => {
  const queryClient = useQueryClient();

  const { isAdminOrTechOperator, isTrader, role } = useUser();

  const [traderId, setTraderId] = useState('');

  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix:
      'features.requisites.payout_requisites_balance_redistribution.details_dialog',
  });
  const { t: tCommon } = useTranslation();
  const { fiatCurrenciesOptions, getFiatCurrencyByCode } = useCurrencies();
  const initialValues = useMemo(
    () => ({
      balanceFiatCurrencyId:
        getFiatCurrencyByCode(FiatCurrencyCode.RUB)?.id || '',
      type: PayoutRequisitesAutomationType.AMobile,
    }),
    [getFiatCurrencyByCode],
  );

  const validationSchema: Yup.ObjectSchema<Values> = useMemo(
    () =>
      Yup.object<Values>().shape({
        traderId: isAdminOrTechOperator
          ? Yup.string().required(tCommon('errors.required'))
          : Yup.string().optional(),
        maxBalance: Yup.number()
          .moreThan(0, tCommon('errors.natural_number'))
          .required(tCommon('errors.required')),
        fromAutomationsMin: Yup.number().optional(),
        fromAutomationsMax: Yup.number().optional(),
        toAutomationsMin: Yup.number().optional(),
        toAutomationsMax: Yup.number().optional(),
        balanceFiatCurrencyId: Yup.string().required(
          tCommon('errors.required'),
        ),
        type: Yup.string()
          .oneOf(values(PayoutRequisitesAutomationType))
          .required(tCommon('errors.required')),
        fromAutomations: Yup.array()
          .of(Yup.string().required(tCommon('errors.required')))
          .min(1, tCommon('errors.required'))
          .required(tCommon('errors.required')),
        toAutomations: Yup.array()
          .of(Yup.string().required(tCommon('errors.required')))
          .min(1, tCommon('errors.required'))
          .required(tCommon('errors.required')),
      }),
    [isAdminOrTechOperator, tCommon],
  );

  const { getLabelWithAccountInfo } = usePayoutAutomationUtils();

  const queryResultAsTrader = useQuery(
    QueryKey.PayoutRequisitesAutomation,
    payoutRequisitesAutomationApi.getAll,
    { enabled: open && isTrader },
  );

  const queryResultAsManager = useQuery(
    [QueryKey.PayoutRequisitesAutomation, traderId],
    () => payoutRequisitesAutomationApi.getAutomationsForTrader(traderId),
    { enabled: open && !!traderId && isAdminOrTechOperator },
  );

  const queryResult = useMemo(() => {
    if (isAdminOrTechOperator) {
      return queryResultAsManager;
    }
    return queryResultAsTrader;
  }, [isAdminOrTechOperator, queryResultAsManager, queryResultAsTrader]);

  const { mutate: create } = useMutation(
    payoutRequisitesBalanceRedistributionRequestsApi.createAsRole(role),
  );

  const typeOptions = useMemo(
    () =>
      map(values(PayoutRequisitesAutomationType), (type) => ({
        label: payoutRequisitesAutomationUtils.getTypeLabel(type),
        value: type,
      })),
    [],
  );

  const handleClose = useCallback(
    (result: CloseFormikDialogResult<Values>) => {
      if (result.ok && result.data) {
        const dto = {
          ...(isAdminOrTechOperator && {
            traderId: result.data.values.traderId,
          }),
          maxBalance: result.data.values.maxBalance,
          fromAutomations: map(result.data.values.fromAutomations, (id) => ({
            id,
          })),
          toAutomations: map(result.data.values.toAutomations, (id) => ({
            id,
          })),
          balanceFiatCurrencyId: result.data.values.balanceFiatCurrencyId,
          type: result.data.values.type,
        };
        const options = {
          onSuccess: () => {
            result.data?.formikHelpers.resetForm();
            queryClient.invalidateQueries(
              QueryKey.PayoutRequisitesBalanceRedistribution,
            );
            onClose(result);
          },
        };
        create(dto, options);
      } else {
        onClose(result);
      }
    },
    [isAdminOrTechOperator, queryClient, onClose, create],
  );

  const getOptions = useCallback(
    (
      anotherOptions: string[],
      {
        area,
        min,
        max,
        fiatCurrencyId,
        type,
        traderId,
      }: {
        area: 'from' | 'to';
        min?: number;
        max?: number;
        fiatCurrencyId?: string;
        type?: PayoutRequisitesAutomationType;
        traderId?: string;
      },
    ) =>
      map(
        filter(
          sortBy(
            queryResult.data || [],
            (automation) => -(automation.expectedBalance || 0),
          ),
          (automation) =>
            isNumber(automation.expectedBalance) &&
            (automation.expectedBalance > 0 || area === 'to') &&
            (!isNumber(min) || automation.expectedBalance >= min) &&
            (!isNumber(max) || automation.expectedBalance <= max) &&
            automation.balanceFiatCurrencyId === fiatCurrencyId &&
            automation.type === type &&
            (!traderId || automation.traderId === traderId),
        ),
        (automation) => ({
          label: getLabelWithAccountInfo(automation),
          value: automation.id,
          disabled: includes(anotherOptions, automation.id),
        }),
      ),
    [getLabelWithAccountInfo, queryResult.data],
  );

  const renderAutomations = useCallback(
    (formik: FormikProps<Values>, area: 'from' | 'to', title: string) => {
      const arrayField = `${area}Automations`;
      const minField:
        | 'fromAutomationsMin'
        | 'toAutomationsMin' = `${area}AutomationsMin`;
      const maxField:
        | 'fromAutomationsMax'
        | 'toAutomationsMax' = `${area}AutomationsMax`;
      const otherArrayField =
        area === 'from' ? 'toAutomations' : 'fromAutomations';

      const selectedOther = formik.values[otherArrayField];
      const min = formik.values[minField];
      const max = formik.values[maxField];

      const options =
        isAdminOrTechOperator && !formik.values.traderId
          ? []
          : getOptions(selectedOther, {
              area,
              min,
              max,
              fiatCurrencyId: formik.values.balanceFiatCurrencyId,
              type: formik.values.type,
              traderId: formik.values.traderId,
            });

      return (
        <FormControls sx={{ flex: 1 }} key={area}>
          <Typography variant="subtitle2">{title}</Typography>
          <FormControls row>
            <FormikNumericField
              name={minField}
              label={t('fields.balance_min')}
              allowNegative={false}
              fullWidth
            />
            <FormikNumericField
              name={maxField}
              label={t('fields.balance_max')}
              allowNegative={false}
              fullWidth
            />
          </FormControls>
          <Card sx={{ p: 2, maxHeight: 300, overflow: 'auto' }}>
            <DataWrapper isEmpty={!options.length}>
              <FieldArray name={arrayField}>
                {() =>
                  map(options, (option) => (
                    <FormikCheckbox
                      key={option.value}
                      name={arrayField}
                      value={option.value}
                      label={option.label}
                      disabled={option.disabled}
                      sx={{ my: 0, display: 'inline-flex' }}
                    />
                  ))
                }
              </FieldArray>
            </DataWrapper>
          </Card>
        </FormControls>
      );
    },
    [isAdminOrTechOperator, t, getOptions],
  );

  const resetSelectedAutomations = useCallback(
    (formik: FormikHelpers<Values>) => {
      formik.setFieldValue('fromAutomations', []);
      formik.setFieldValue('toAutomations', []);
    },
    [],
  );

  const handleTraderIdChange = useCallback(
    (value: string, formik: FormikProps<Values>) => {
      setTraderId(value);
      resetSelectedAutomations(formik);
    },
    [resetSelectedAutomations],
  );

  return (
    <FormikDialog
      dialogProps={{
        open,
        title: t('title'),
        maxWidth: 'md',
        onClose: handleClose,
      }}
      formikProps={{
        validateOnMount: true,
        initialValues,
        validationSchema,
      }}
    >
      {({ formik }) => (
        <Fragment>
          <DataWrapper
            queryResult={
              isAdminOrTechOperator ? undefined : queryResultAsTrader
            }
          >
            <Fragment>
              <FormControls>
                {isAdminOrTechOperator && (
                  <TraderSelect
                    sx={{ maxWidth: 300 }}
                    name="traderId"
                    required
                    onChange={(value) => handleTraderIdChange(value, formik)}
                  />
                )}
                <div className="tw-flex tw-flex-col md:tw-flex-row tw-gap-4">
                  <div className="tw-flex-1">
                    <FormikSelect
                      label={t('fields.currency')}
                      fullWidth
                      name="balanceFiatCurrencyId"
                      required
                      options={fiatCurrenciesOptions}
                      onChange={() => resetSelectedAutomations(formik)}
                    />
                  </div>
                  <div className="tw-flex-1">
                    <FormikSelect
                      label={t('fields.automation_type')}
                      name="type"
                      fullWidth
                      options={typeOptions}
                      required
                      onChange={() => resetSelectedAutomations(formik)}
                    />
                  </div>
                  <FormikNumericField
                    name="maxBalance"
                    label={t('fields.max_wallet_balance')}
                    sx={{ flex: 1 }}
                    fullWidth
                    required
                    allowNegative={false}
                  />
                </div>
                <DataWrapper
                  queryResult={
                    isAdminOrTechOperator ? queryResultAsManager : undefined
                  }
                >
                  <div className="tw-flex tw-flex-col lg:tw-flex-row tw-gap-4">
                    {renderAutomations(
                      formik,
                      'from',
                      t('fields.from_automations'),
                    )}
                    <div className="tw-hidden lg:tw-block">
                      <EastIcon />
                    </div>
                    {renderAutomations(
                      formik,
                      'to',
                      t('fields.to_automations'),
                    )}
                  </div>
                </DataWrapper>
              </FormControls>
            </Fragment>
          </DataWrapper>
        </Fragment>
      )}
    </FormikDialog>
  );
};
